import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { CreditCard, Plug, Video, CalendarCheck, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';

type FlowId = 'integrations' | 'schedule' | 'pay';

const flows: {
  id: FlowId;
  label: string;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
}[] = [
  {
    id: 'integrations',
    label: 'Core integrations',
    icon: Plug,
    eyebrow: 'One rhythm, many tools',
    title: 'Payments, meetings, and growth tools in sync',
    body:
      'Arrangelly connects billing, video lessons, newsletter, blog, analytics, and your calendar so nothing falls between the cracks.',
    bullets: [
      'Lemon-ready payments & lesson credits',
      'Meeting links tied to bookings',
      'Newsletter & blog without a second login',
    ],
  },
  {
    id: 'schedule',
    label: 'Book & join',
    icon: CalendarCheck,
    eyebrow: 'From slot to session',
    title: 'Students pick a time — you both land in the call',
    body:
      'Availability, reminders, and join links stay attached to each booking so the lesson starts on time, every time.',
    bullets: [
      'Calendar reflects your real availability',
      'Automated confirmations & nudges',
      'One-tap join for teachers and students',
    ],
  },
  {
    id: 'pay',
    label: 'Get paid',
    icon: CreditCard,
    eyebrow: 'Transparent money flow',
    title: 'Invoices, packages, and subscriptions — clearly',
    body:
      'Whether it’s a single lesson, a bundle, or a platform plan, your students always know what they’re paying for.',
    bullets: [
      'Packages & lesson credits',
      'Platform plans that match your stage',
      'Receipts students can trust',
    ],
  },
];

const IntegrationsViz = () => {
  const nodes = [
    { icon: CreditCard, label: 'Pay', delay: 0, className: 'left-[8%] top-[12%]' },
    { icon: Video, label: 'Meet', delay: 0.15, className: 'right-[10%] top-[18%]' },
    { icon: CalendarCheck, label: 'Book', delay: 0.3, className: 'left-[12%] bottom-[14%]' },
  ];

  return (
    <div className="relative mx-auto aspect-[4/3] max-h-[280px] w-full max-w-md rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-violet-50/40 to-slate-50 p-4 shadow-inner">
      <motion.div
        className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/90 to-secondary/80 text-primary-foreground shadow-lg shadow-primary/25"
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Sparkles className="h-9 w-9" strokeWidth={1.5} />
      </motion.div>

      {nodes.map(({ icon: Icon, label, delay, className }) => (
        <motion.div
          key={label}
          className={`absolute flex flex-col items-center gap-1 ${className}`}
          initial={{ opacity: 0, scale: 0.85, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay, type: 'spring', stiffness: 320, damping: 22 }}
        >
          <motion.div
            className="flex h-12 w-12 items-center justify-center rounded-xl border border-violet-200 bg-white shadow-sm"
            animate={{ y: [0, -4, 0] }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: delay * 2,
            }}
          >
            <Icon className="h-6 w-6 text-primary" />
          </motion.div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
        </motion.div>
      ))}

      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-primary/20"
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.25, duration: 0.5 }}
      />
      <motion.div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[72%] w-[72%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-primary/10"
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
      />
    </div>
  );
};

const ScheduleViz = () => (
  <div className="relative mx-auto aspect-[4/3] max-h-[280px] w-full max-w-md rounded-2xl border border-slate-200/90 bg-white p-4 shadow-inner">
    <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
      <span className="text-xs font-semibold text-slate-800">This week</span>
      <motion.span
        className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800"
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Live
      </motion.span>
    </div>
    <div className="grid grid-cols-4 gap-2 text-center text-[9px] font-medium uppercase tracking-wide text-slate-400">
      {['Mon', 'Tue', 'Wed', 'Thu'].map((d) => (
        <span key={d}>{d}</span>
      ))}
    </div>
    <div className="mt-2 grid grid-cols-4 gap-2">
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className={`h-14 rounded-xl border ${
            i === 2
              ? 'border-primary/40 bg-gradient-to-b from-violet-50 to-white shadow-md shadow-violet-200/50'
              : 'border-slate-100 bg-slate-50/80'
          }`}
          animate={i === 2 ? { scale: [1, 1.03, 1] } : {}}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
    <motion.div
      className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-3 py-2.5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 24 }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
          <Video className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="min-w-0 text-left">
          <p className="text-xs font-semibold text-slate-800 truncate">Piano · Wed 4:00 PM</p>
          <p className="text-[10px] text-slate-500">Meeting link ready</p>
        </div>
      </div>
      <motion.button
        type="button"
        className="shrink-0 rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white shadow-md"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
        animate={{ boxShadow: ['0 4px 12px rgba(16, 185, 129, 0.25)', '0 6px 20px rgba(16, 185, 129, 0.45)', '0 4px 12px rgba(16, 185, 129, 0.25)'] }}
        transition={{ duration: 2.2, repeat: Infinity }}
      >
        Join
      </motion.button>
    </motion.div>
    <motion.div
      className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white shadow-md"
      animate={{ rotate: [0, 8, -8, 0] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
    >
      ↔
    </motion.div>
  </div>
);

const PayViz = () => (
  <div className="relative mx-auto aspect-[4/3] max-h-[280px] w-full max-w-md rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50 p-4 shadow-inner">
    <div className="mb-3 flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-800">Checkout preview</span>
      <span className="text-[10px] text-slate-400">Secure</span>
    </div>
    <div className="space-y-2">
      {[
        { label: '4 × Lesson pack', amount: '€120.00', delay: 0 },
        { label: 'Processing fee', amount: '€0.00', delay: 0.08 },
        { label: 'Paid to your studio', amount: '€120.00', delay: 0.16 },
      ].map((row) => (
        <motion.div
          key={row.label}
          className="relative flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2.5 text-sm shadow-sm"
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: row.delay, type: 'spring', stiffness: 300, damping: 28 }}
        >
          <span className="text-slate-600">{row.label}</span>
          <span className="font-medium tabular-nums text-slate-900">{row.amount}</span>
        </motion.div>
      ))}
    </div>
    <motion.div
      className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 py-3 text-emerald-800"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.55, type: 'spring', stiffness: 280, damping: 20 }}
    >
      <motion.div
        initial={{ scale: 0, rotate: -45 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.65, type: 'spring', stiffness: 400, damping: 18 }}
      >
        <CheckCircle2 className="h-6 w-6 text-emerald-600" />
      </motion.div>
      <span className="text-sm font-semibold">Payment confirmed</span>
    </motion.div>
    <motion.div
      className="pointer-events-none absolute inset-x-8 bottom-8 h-8 rounded-full bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 blur-xl"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2.5, repeat: Infinity }}
    />
  </div>
);

const illustrations: Record<FlowId, React.FC> = {
  integrations: IntegrationsViz,
  schedule: ScheduleViz,
  pay: PayViz,
};

const HomeFlowShowcase: React.FC = () => {
  const [active, setActive] = useState<FlowId>('integrations');
  const flow = flows.find((f) => f.id === active)!;
  const Viz = illustrations[active];

  return (
    <div className="border-t border-slate-200/80 bg-white/50 backdrop-blur-sm">
      <div className="container mx-auto px-4 py-14 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6 }}
          className="mb-10 text-center max-w-2xl mx-auto"
        >
          <p className="text-sm font-medium uppercase tracking-[0.2em] text-violet-600 mb-2">How Arrangelly works</p>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">See the flow, not just the feature list</h2>
          <p className="text-slate-600">
            Tap a story — watch a lightweight sketch of the product. Built with motion so you can feel the journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 max-w-4xl mx-auto mb-10">
          {flows.map((f) => {
          const isOn = active === f.id;
          const TabIcon = f.icon;
          return (
            <motion.button
              key={f.id}
              type="button"
              onClick={() => setActive(f.id)}
              className={`relative text-left rounded-[1.75rem] border px-5 py-4 transition-colors ${
                isOn
                  ? 'border-primary/35 bg-violet-50/90 shadow-md shadow-violet-200/40'
                  : 'border-slate-200/90 bg-white/80 hover:border-slate-300 hover:bg-white'
              }`}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.99 }}
            >
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <TabIcon className={`h-4 w-4 ${isOn ? 'text-primary' : 'text-slate-400'}`} />
                {f.label}
                <ArrowRight className={`ml-auto h-4 w-4 transition-transform ${isOn ? 'translate-x-0.5 text-primary' : 'text-slate-300'}`} />
              </span>
              <span className="mt-1 block text-xs text-slate-500 line-clamp-2">{f.eyebrow}</span>
            </motion.button>
          );
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={active + '-copy'}
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="space-y-5"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">{flow.eyebrow}</p>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">{flow.title}</h3>
              <p className="text-slate-600 leading-relaxed">{flow.body}</p>
              <ul className="space-y-3">
                {flow.bullets.map((b, i) => (
                  <motion.li
                    key={b}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.07 }}
                    className="flex gap-3 text-sm text-slate-700"
                  >
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-primary to-secondary" />
                    {b}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div
              key={active + '-viz'}
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -8 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="glass-panel rounded-[1.75rem] p-5 md:p-8"
            >
              <Viz />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default HomeFlowShowcase;
